<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['cancel', 'update:show']);

const DEFAULT_MESSAGES = 200;
const MAX_MESSAGES = 1000;

const { t } = useI18n();

const messages = ref(DEFAULT_MESSAGES);
const isSubmitting = ref(false);

const onCancel = () => {
  emit('cancel');
};

const onSubmit = async () => {
  isSubmitting.value = true;
  try {
    // Fire-and-forget: the sync runs in a background job, so there's nothing to await here
    // beyond enqueueing it - new messages show up once the agent refreshes the conversation.
    await ConversationAPI.syncEvolutionHistory(
      props.conversationId,
      messages.value
    );
    useAlert(t('CONTACT_PANEL.SYNC_EVOLUTION_HISTORY_SUCCESS'));
    onCancel();
  } catch (error) {
    useAlert(t('CONTACT_PANEL.SYNC_EVOLUTION_HISTORY_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <woot-modal
    :show="show"
    :on-close="onCancel"
    @update:show="$emit('update:show', $event)"
  >
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CONTACT_PANEL.SYNC_EVOLUTION_HISTORY')"
        :header-content="$t('CONTACT_PANEL.SYNC_EVOLUTION_HISTORY_MODAL.DESC')"
      />
      <form class="w-full" @submit.prevent="onSubmit">
        <div class="w-full">
          <label>
            {{
              $t('CONTACT_PANEL.SYNC_EVOLUTION_HISTORY_MODAL.MESSAGES_LABEL')
            }}
            <input
              v-model.number="messages"
              type="number"
              min="1"
              :max="MAX_MESSAGES"
            />
          </label>
          <p class="help-text">
            {{
              $t(
                'CONTACT_PANEL.SYNC_EVOLUTION_HISTORY_MODAL.MESSAGES_SUBTITLE',
                { max: MAX_MESSAGES }
              )
            }}
          </p>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CONTACT_PANEL.SYNC_EVOLUTION_HISTORY_MODAL.CANCEL')"
            @click.prevent="onCancel"
          />
          <NextButton
            type="submit"
            :is-loading="isSubmitting"
            :label="$t('CONTACT_PANEL.SYNC_EVOLUTION_HISTORY_MODAL.SUBMIT')"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
